<template>
  <component
    :is="cardComponent"
    v-bind="cardAttrs"
    class="vdb-vcard"
    humblytics="video-card"
    @click="handleClick"
    @keydown.enter="handleKeyboardActivate"
    @keydown.space="handleKeyboardSpace"
  >
    <div class="vdb-vcard-thumb" humblytics="video-card">
      <img
        v-if="thumbnailSrc"
        :src="thumbnailSrc"
        :alt="video.simple_description || video.description || 'Video thumbnail'"
        humblytics="video-card"
        loading="lazy"
        decoding="async"
        @error="thumbError = true"
      />
      <div class="overlay" humblytics="video-card"></div>

      <div v-if="hookView" class="vdb-hook-view-overlay">
        <div
          v-for="channel in hookChannels"
          :key="channel.type"
          class="vdb-hook-channel"
        >
          <VdbIcon :name="channel.icon" :size="14" :label="channel.label" />
          <p>{{ channel.content }}</p>
        </div>
        <span v-if="hookChannels.length === 0" class="vdb-hook-view-empty">No hook channels</span>
      </div>

      <!-- TL: platform + handle -->
      <div v-if="!hookView" class="vdb-pin tl">
        <span class="vdb-pill dark">
          <VdbPlatformIcon :platform="video.platform" :size="11"/>
          @{{ displayHandle }}
        </span>
      </div>

      <!-- TL-2: concept pill -->
      <div v-if="!hookView && conceptName" class="vdb-pin tl-2">
        <span class="vdb-pill concept">{{ conceptName }}</span>
      </div>

      <!-- TR: date -->
      <div v-if="!hookView && dateDisplay" class="vdb-pin tr">
        <span class="vdb-pill dark">{{ dateDisplay }}</span>
      </div>

      <!-- BL stack: ad flag + likes + views -->
      <div class="vdb-pin bl stack">
        <span v-if="video.ad_flag" class="vdb-pill ad" title="Low engagement suggests promoted content">📣 Ad</span>
        <span v-if="likesDisplay" class="vdb-pill dark">
          <span style="color: #ff6b8a; margin-right: 2px">&hearts;</span>{{ likesDisplay }}
        </span>
        <span v-if="viewsDisplay" class="vdb-pill dark">
          <span style="margin-right: 2px">&#9654;</span>{{ viewsDisplay }}
        </span>
      </div>

      <!-- BR-mid: outlier — Cut30 signature treatment -->
      <div v-if="outlierDisplay" class="vdb-pin br-mid">
        <span class="vdb-pill-outlier"><span class="dot"></span> {{ outlierDisplay }}</span>
      </div>

      <!-- BR: engagement -->
      <div v-if="engagementDisplay" class="vdb-pin br">
        <span class="vdb-pill dark">&#128293; {{ engagementDisplay }}</span>
      </div>
    </div>

    <div v-if="!hideBody" class="vdb-vcard-body" humblytics="video-card">
      <div class="vdb-vcard-title" humblytics="video-card">{{ titleText }}</div>
    </div>
  </component>
</template>

<script>
import VdbPlatformIcon from './VdbPlatformIcon.vue';
import VdbIcon from './VdbIcon.vue';
import { buildVideoUrl, optimizeThumbnailUrl } from '@/utils/videoUrl.js';

export default {
  name: 'VdbVideoCard',
  components: { VdbIcon, VdbPlatformIcon },
  emits: ['click'],
  props: {
    video: { type: Object, required: true },
    hideBody: { type: Boolean, default: false },
    noNavigate: { type: Boolean, default: false },
    hookView: { type: Boolean, default: false },
  },
  data() {
    return { thumbError: false };
  },
  computed: {
    videoHref() {
      return buildVideoUrl(this.video);
    },
    isLinkedCard() {
      return !this.noNavigate && !!this.videoHref;
    },
    cardComponent() {
      return this.isLinkedCard ? 'router-link' : 'div';
    },
    cardAttrs() {
      const label = this.isLinkedCard ? `Open video: ${this.titleText}` : `Select video: ${this.titleText}`;
      if (this.isLinkedCard) {
        return {
          to: this.videoHref,
          'aria-label': label
        };
      }
      return {
        role: 'button',
        tabindex: '0',
        'aria-label': label
      };
    },
    thumbnailSrc() {
      if (this.thumbError) return null;
      const sources = [
        this.video.thumbnail,
        this.video.thumbnail_url,
        this.video.thumbnailUrl,
      ].filter(Boolean);
      if (!sources.length) return null;
      const url = sources[0];
      if (typeof url !== 'string') return null;
      if (url.startsWith('/api/proxy/image')) return url;
      if (url.startsWith('/api/img')) return optimizeThumbnailUrl(url, 400);
      if (url.startsWith('http')) {
        if (url.includes('instagram.com') || url.includes('cdninstagram.com') || url.includes('scontent') || url.includes('tiktokcdn')) {
          return `/api/proxy/image?url=${encodeURIComponent(url)}`;
        }
        const optimized = optimizeThumbnailUrl(url, 400);
        if (optimized !== url) return optimized;
      }
      return url;
    },
    displayHandle() {
      const u = this.video.author_username || this.video.username || '';
      return u.startsWith('@') ? u.slice(1) : u;
    },
    conceptName() {
      const c = this.video.concepts;
      if (Array.isArray(c) && c.length) return c[0];
      if (typeof c === 'string' && c) return c.split(',')[0].trim();
      const dbConcepts = this.video.db_analysis?.concepts;
      if (Array.isArray(dbConcepts) && dbConcepts.length) {
        return dbConcepts[0]?.match || dbConcepts[0]?.aiSuggestion || null;
      }
      return null;
    },
    dateDisplay() {
      const d = this.video.post_date || this.video.posted_at;
      if (!d) return null;
      const date = new Date(d);
      if (isNaN(date)) return null;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    outlierDisplay() {
      const v = this.video.outlier_score;
      if (v === null || v === undefined) return null;
      const num = Number(v);
      if (!Number.isFinite(num) || num <= 1) return null;
      return `${num.toFixed(1)}×`;
    },
    likesDisplay() {
      return this.formatCompact(this.video.likes);
    },
    viewsDisplay() {
      if (!this.video.views) return null;
      return this.formatCompact(this.video.views);
    },
    engagementDisplay() {
      const v = this.video.engagement || this.video.engagement_rate;
      if (v === null || v === undefined) return null;
      const num = Number(v);
      if (!Number.isFinite(num)) return null;
      const pct = num > 1 ? num : num * 100;
      if (pct <= 1) return null;
      return `${pct.toFixed(1)}%`;
    },
    formatName() {
      return this.video.format || 'Video';
    },
    timeAgo() {
      const d = this.video.post_date || this.video.posted_at;
      if (!d) return '';
      const diff = Date.now() - new Date(d).getTime();
      const days = Math.floor(diff / 86400000);
      if (days < 1) return 'today';
      if (days === 1) return '1d ago';
      if (days < 30) return `${days}d ago`;
      if (days < 365) return `${Math.floor(days / 30)}mo ago`;
      return `${Math.floor(days / 365)}y ago`;
    },
    titleText() {
      return this.video.simple_description || this.video.description || this.video.title || 'Untitled';
    },
    hookDominance() {
      return this.video.hook_dominance || null;
    },
    hookChannels() {
      const definitions = [
        { type: 'title', label: 'Title Hook', icon: 'type' },
        { type: 'verbal', label: 'Verbal Hook', icon: 'message-circle' },
      ];
      const fallbackContent = {
        title: this.hookDominance?.channels?.title ?? this.video.hook_title_content,
        verbal: this.hookDominance?.channels?.verbal ?? this.video.hook_verbal_content,
      };

      return definitions
        .map(channel => ({ ...channel, content: fallbackContent[channel.type] }))
        .filter(channel => typeof channel.content === 'string' && channel.content.trim())
        .map(channel => ({ ...channel, content: channel.content.trim() }));
    },
  },
  methods: {
    handleClick() {
      this.$emit('click', this.video);
    },
    handleKeyboardActivate() {
      if (this.isLinkedCard) return;
      this.handleClick();
    },
    handleKeyboardSpace(event) {
      if (this.isLinkedCard) return;
      event.preventDefault();
      this.handleClick();
    },
    formatCompact(value) {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      if (!Number.isFinite(num) || num <= 1) return null;
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
      return num.toLocaleString();
    },
  },
};
</script>

<style scoped>
.vdb-vcard {
  color: inherit;
  text-decoration: none;
}

.vdb-vcard:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}
</style>
